import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Flame,
  Beef,
  Droplets,
  Utensils,
  TrendingDown,
  Minus,
  TrendingUp,
} from "lucide-react";
import api from "@/lib/api";
import Skeleton from "@/components/Skeleton";
import NutritionDisclaimer from "@/components/NutritionDisclaimer";

const GOALS = [
  { id: "cut",      label: "Lose fat",  icon: TrendingDown },
  { id: "maintain", label: "Maintain",  icon: Minus },
  { id: "bulk",     label: "Build",     icon: TrendingUp },
];

const ACTIVITIES = [
  { id: "sedentary",      label: "Sedentary" },
  { id: "lightly_active", label: "Lightly active" },
  { id: "active",         label: "Active" },
  { id: "very_active",    label: "Very active" },
];

export default function NutritionScreen() {
  const navigate = useNavigate();
  const [goal, setGoal] = useState("maintain");
  const [activity, setActivity] = useState("lightly_active");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/nutrition/recommend?goal=${goal}&activity=${activity}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ ready: false, missing: ["network"] }))
      .finally(() => setLoading(false));
  }, [goal, activity]);

  return (
    <div className="screen with-nav fade-in" data-testid="nutrition-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/progress")} aria-label="Back" data-testid="nutrition-back">
          <ChevronLeft size={18}/>
        </button>
        <div className="logo-mark">NUTRITION</div>
        <span className="premium-pill">FREE</span>
      </div>

      <div className="eyebrow" style={{ marginBottom: 8 }}>NOVA · FUEL</div>
      <h2 className="h1" style={{ fontSize: 28, marginBottom: 20, lineHeight: 1.15 }}>
        Eat for<br/>
        <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>the glow.</em>
      </h2>

      <NutritionDisclaimer />

      {/* Goal chips */}
      <div className="coach-topics" data-testid="nutrition-goals" style={{ marginBottom: 4 }}>
        {GOALS.map((g) => {
          const Icon = g.icon;
          return (
            <button
              key={g.id}
              type="button"
              className={`coach-topic ${goal === g.id ? "active" : ""}`}
              onClick={() => setGoal(g.id)}
              data-testid={`nutrition-goal-${g.id}`}
            >
              <Icon size={13} strokeWidth={1.8}/>
              <span>{g.label}</span>
            </button>
          );
        })}
      </div>

      <div className="coach-topics" data-testid="nutrition-activities" style={{ marginBottom: 10 }}>
        {ACTIVITIES.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`coach-topic ${activity === a.id ? "active" : ""}`}
            onClick={() => setActivity(a.id)}
            data-testid={`nutrition-activity-${a.id}`}
          >
            {a.label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}>
          <Skeleton w="100%" h={160} r={22}/>
          <Skeleton w="100%" h={80} r={16}/>
          <Skeleton w="100%" h={200} r={18}/>
        </div>
      )}

      {!loading && data && !data.ready && (
        <div className="photo-empty" data-testid="nutrition-onboarding" style={{ marginTop: 20 }}>
          <div className="photo-empty-icon"><Utensils size={18} strokeWidth={1.8}/></div>
          <div className="body" style={{ color: "#fff", marginBottom: 6 }}>Finish your profile first</div>
          <div className="tiny" style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.08em", marginBottom: 14 }}>
            We need {data.missing?.join(" / ") || "your details"} to calculate your numbers.
          </div>
          <button
            type="button"
            className="btn btn-gold"
            onClick={() => navigate("/profile-setup")}
            data-testid="nutrition-profile-cta"
          >
            Build my profile
          </button>
        </div>
      )}

      {!loading && data?.ready && (
        <>
          {/* Calories hero */}
          <div className="glow-empty-hero" data-testid="nutrition-hero" style={{ textAlign: "center", padding: "26px 22px" }}>
            <div className="glow-empty-badge"><Flame size={14} strokeWidth={2}/> Calories today</div>
            <div style={{ marginTop: 18 }}>
              <div className="numeric" style={{ fontFamily: "'Fraunces', serif", fontSize: 56, color: "#fff", lineHeight: 1, letterSpacing: "-0.04em" }}>
                {data.calories_today}
              </div>
              <div className="tiny" style={{ color: "var(--gold)", letterSpacing: "0.22em", textTransform: "uppercase", marginTop: 6 }}>
                kcal · {data.goal}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-around", marginTop: 18, gap: 14 }}>
              {Object.entries(data.targets).map(([k, v]) => (
                <div key={k} style={{ textAlign: "center" }}>
                  <div className="numeric" style={{ fontSize: 16, color: data.goal === k ? "var(--gold)" : "rgba(255,255,255,0.55)" }}>{v}</div>
                  <div className="tiny" style={{ color: "rgba(255,255,255,0.4)", letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 2 }}>{k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Macros */}
          <div className="outfit-section" data-testid="nutrition-macros">
            <div className="eyebrow" style={{ marginBottom: 12 }}>DAILY TARGETS</div>
            <div className="glow-sub-grid">
              <div className="glow-sub-card" data-testid="nutrition-protein">
                <div className="glow-sub-card-icon"><Beef size={16} strokeWidth={1.8}/></div>
                <div className="glow-sub-card-label">Protein</div>
                <div className="glow-sub-card-value">{data.macros.protein_g}g</div>
                <div className="glow-sub-card-trend">{data.macros.protein_g * 4} kcal</div>
              </div>
              <div className="glow-sub-card" data-testid="nutrition-water">
                <div className="glow-sub-card-icon"><Droplets size={16} strokeWidth={1.8}/></div>
                <div className="glow-sub-card-label">Water</div>
                <div className="glow-sub-card-value">{(data.water_ml / 1000).toFixed(1)}L</div>
                <div className="glow-sub-card-trend">{data.water_glasses_250ml} glasses</div>
              </div>
              <div className="glow-sub-card">
                <div className="glow-sub-card-icon"><Utensils size={16} strokeWidth={1.8}/></div>
                <div className="glow-sub-card-label">Carbs</div>
                <div className="glow-sub-card-value">{data.macros.carbs_g}g</div>
                <div className="glow-sub-card-trend">{data.macros.carbs_g * 4} kcal</div>
              </div>
              <div className="glow-sub-card">
                <div className="glow-sub-card-icon"><Utensils size={16} strokeWidth={1.8}/></div>
                <div className="glow-sub-card-label">Fats</div>
                <div className="glow-sub-card-value">{data.macros.fats_g}g</div>
                <div className="glow-sub-card-trend">{data.macros.fats_g * 9} kcal</div>
              </div>
            </div>
          </div>

          {/* Meal ideas */}
          <div className="outfit-section" data-testid="nutrition-meals">
            <div className="eyebrow" style={{ marginBottom: 12 }}>MEAL IDEAS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {data.meals.map((m, i) => (
                <div key={i} className="card" style={{ padding: 14 }} data-testid={`nutrition-meal-${i}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div className="h3" style={{ color: "#fff" }}>{m.name}</div>
                    <div className="tiny" style={{ color: "var(--gold)", letterSpacing: "0.08em" }}>
                      {m.approx_calories} kcal · {m.approx_protein_g}g P
                    </div>
                  </div>
                  <div className="body-sm" style={{ color: "rgba(255,255,255,0.7)", marginTop: 8 }}>
                    {m.items.join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why it matters */}
          <div className="outfit-section" data-testid="nutrition-why">
            <div className="eyebrow" style={{ marginBottom: 12 }}>WHY IT MATTERS</div>
            <ul className="bullet-list">
              {data.why_it_matters.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
